import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setActiveLugs } from 'app/slices/ahuBoardSlice';
import { ahuBoardApi, usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import {
  useGetLugParamsQuery,
  useCreateTunnelLugMutation,
  useCreateLugSettingMutation,
  usePatchLugSettingMutation,
  useDeleteLugSettingMutation,
} from 'app/services/ahuBoardFormsApi';
import type { IAhuBoardTunnel, IAhuBoardUnit, IAhuTunnelLugPlacement, ICmsAhuDoc } from 'models';
import { usePatchAhuUnitMutation } from 'app/services/ahuBoardFormsApi';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { getVisibleTunnelIndexes } from '../../addressing';
import { lugsOnForTunnel } from '../../hybridLugFields';
import { cmsBaseList } from '../../forms/cmsDoc';
import { catalogLabel } from '../../forms/formUtils';
import { UniversalPopup, DeleteConfirmPopup } from '../SharedPopups';

/**
 * Phase 6e1 — Base & Lugs / lifting-lug pane (§7.3.2, tab.lifting.lug.js +
 * tab.lifting.lug.tmpl, markup faithful). THE lug-selection receiving end:
 * consumes 6d's `lugs.activeLugIds` slice seam + the canvas
 * `change:active:lugs` broadcast (tab switch / accordion-open halves are the
 * canvas's slice writes; this pane maps lug ids → position records, retargets
 * the tunnel pill, renders single or merged "varies..." settings).
 *
 * Lug-defaults check/uncheck warnings use the EXACT legacy copy with
 * revert-then-confirm semantics (config block ported verbatim; parity 183).
 */

const LUG_DEFAULTS_COPY = {
  uncheck: {
    title: 'Warning!',
    content:
      "If you uncheck the Defaults, you will be granted the ability to customize the placement and features of your lugs, but you will take the risk of violating the manufacturer's lug application rules.",
    actionText: 'UNCHECK',
    newValue: false,
  },
  check: {
    title: 'Warning!',
    content:
      "If you check the Defaults, you will lose all customization applied to the placement and features of the lugs and reset all values to the manufacturer's standards.",
    actionText: 'CHECK',
    newValue: true,
  },
} as const;

type LugPosition = Record<string, unknown> & { id?: number | string };

interface LiftingLugPaneProps {
  ahu: IAhuBoardUnit;
  tunnels: IAhuBoardTunnel[];
  cms: ICmsAhuDoc | undefined;
  open: boolean;
  /**
   * Hybrid AHUs only: the tunnel to operate on, following the AHU-wide
   * selected section (there is no independent tunnel picker for hybrid —
   * see BasePanel). Ignored for non-hybrid AHUs, which keep their own
   * pill-driven selection below.
   */
  hybridActiveTunnelId?: number | null;
}

const MERGE_KEYS = [
  'attachment_name',
  'base_direction',
  'x',
  'y',
  'model',
  'max_weight_per_lug',
  'max_center_spacing',
  'min_dist_from_base_edge',
] as const;

/** mergeObjects — differing values → null ("varies..."). */
function mergeLugs(lugs: LugPosition[]): LugPosition {
  const merged: LugPosition = { ...lugs[0] };
  MERGE_KEYS.forEach((key) => {
    const first = lugs[0]?.[key];
    if (!lugs.every((lug) => lug[key] === first)) merged[key] = null;
  });
  return merged;
}

const LiftingLugPane = ({
  ahu,
  tunnels,
  cms,
  open,
  hybridActiveTunnelId,
}: LiftingLugPaneProps) => {
  const isHybrid = !!ahu.is_hybrid;
  const dispatch = useAppDispatch();
  const activeLugIds = useAppSelector((s) => s.ahuBoard.lugs.activeLugIds);
  const activeLugTunnelId = useAppSelector((s) => s.ahuBoard.lugs.activeTunnelId);
  const { data: lugParams } = useGetLugParamsQuery();
  const [patchTunnel] = usePatchAhuTunnelMutation();
  const [patchAhuUnit] = usePatchAhuUnitMutation();
  const [createTunnelLug] = useCreateTunnelLugMutation();
  const [createLugSetting] = useCreateLugSettingMutation();
  const [patchLugSetting] = usePatchLugSettingMutation();
  const [deleteLugSetting] = useDeleteLugSettingMutation();
  const [addLugKind, setAddLugKind] = useState<string>('');

  // Based tunnels only get lug models (parity 121) — that is about placements,
  // NOT about the tunnel strip: legacy renders a pill for EVERY tunnel
  // (`tunnels: tunnels.toJSON()`, tab.lifting.lug:307) and only filters when
  // building lug models. Filtering the strip too hid every tunnel that has yet
  // to be given a Base — including a freshly added one, whose Base you set
  // from this very pane.
  const basedTunnels = useMemo(() => tunnels.filter((t) => t.base_id), [tunnels]);
  const [pillActiveTunnelId, setPillActiveTunnelId] = useState<number | null>(
    tunnels[0]?.id ?? null
  );
  // Hybrid AHUs have no independent tunnel picker — the active tunnel follows
  // the AHU-wide selected section (BasePanel uses the same source), passed in
  // from TabAhu as `hybridActiveTunnelId`.
  const activeTunnelId = isHybrid
    ? hybridActiveTunnelId ?? tunnels[0]?.id ?? null
    : pillActiveTunnelId;
  const [placements, setPlacements] = useState<Record<number, IAhuTunnelLugPlacement>>({});
  const [pendingRecalc, setPendingRecalc] = useState<Record<number, boolean>>({});
  const [recalcRunning, setRecalcRunning] = useState(false);
  const [showCmsInfo, setShowCmsInfo] = useState(false);
  const [defaultsPopup, setDefaultsPopup] = useState<'check' | 'uncheck' | null>(null);
  const [deletePopup, setDeletePopup] = useState(false);
  const placementsRef = useRef(placements);
  placementsRef.current = placements;

  const activeTunnel = tunnels.find((t) => t.id === activeTunnelId);

  // Pill follows the canvas cross-tunnel lug pick (§13 row 14). Non-hybrid only
  // — hybrid's active tunnel is derived from the section selection instead.
  useEffect(() => {
    if (isHybrid) return;
    if (activeLugTunnelId != null && activeLugTunnelId !== activeTunnelId) {
      setPillActiveTunnelId(activeLugTunnelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHybrid, activeLugTunnelId]);

  // Adding or deleting a tunnel re-renders this pane through the refreshed
  // tunnel list, so the strip follows on its own; what does not is the
  // selection, which can be left pointing at a tunnel that is gone. Legacy
  // re-homed it to the first tunnel (updateTunnels:256-262) — same here, and it
  // also seeds the selection when the board opens with no tunnels at all.
  // Non-hybrid only — hybrid re-derives from the section selection on its own.
  useEffect(() => {
    if (isHybrid) return;
    if (!tunnels.length) return;
    if (tunnels.some((t) => t.id === activeTunnelId)) return;
    setPillActiveTunnelId(tunnels[0].id);
    dispatch(setActiveLugs({ ids: [], tunnelId: tunnels[0].id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHybrid, tunnels, activeTunnelId]);

  // Tunnel-pill pagination (tab.lifting.lug.js: MAX_VISIBLE_TUNNELS_STANDARD,
  // getVisibleTunnelIndexes/ensureActiveTunnelVisible/shiftVisibleTunnels).
  // Legacy's tunnels-row is itself `{{#unless is_hybrid}}`-gated (this
  // component's own early `if (isHybrid) return` above matches that), so
  // only the non-hybrid MAX_VISIBLE_TUNNELS_STANDARD=6 value is ever
  // reachable from this render path — hybrid AHUs get no pill strip at all,
  // in both legacy and here (their tunnel selection follows the AHU-wide
  // section pick instead, via hybridActiveTunnelId).
  const maxVisibleTunnels = 6;
  const activeTunnelIndex = Math.max(
    0,
    tunnels.findIndex((t) => t.id === activeTunnelId)
  );
  const [visibleTunnelStart, setVisibleTunnelStart] = useState(0);

  // Re-centers the pagination window on the active tunnel whenever it (or the
  // tunnel list) changes, so the selection is always visible regardless of
  // where it's coming from (a pill click, or a canvas lug selection jumping
  // to a tunnel that may currently be scrolled out of view).
  useEffect(() => {
    if (!tunnels.length) {
      setVisibleTunnelStart(0);
      return;
    }
    setVisibleTunnelStart(getVisibleTunnelIndexes(activeTunnelIndex, tunnels.length, maxVisibleTunnels).start);
  }, [activeTunnelIndex, tunnels.length, maxVisibleTunnels]);

  // Shifts the visible pill window by one position without changing which
  // tunnel is active (arrow-click nav).
  const shiftVisibleTunnels = (direction: number) => {
    const total = tunnels.length;
    const maxStart = Math.max(0, total - maxVisibleTunnels);
    setVisibleTunnelStart((prev) => Math.max(0, Math.min(maxStart, prev + direction)));
  };
  const visibleTunnels = tunnels.slice(visibleTunnelStart, visibleTunnelStart + maxVisibleTunnels);
  const canNavTunnelsLeft = visibleTunnelStart > 0;
  const canNavTunnelsRight = visibleTunnelStart + maxVisibleTunnels < tunnels.length;

  const fetchPlacement = (tunnelId: number, force = true) =>
    dispatch(
      ahuBoardApi.endpoints.getTunnelLugPlacement.initiate(tunnelId, {
        forceRefetch: force,
      })
    )
      .unwrap()
      .then((placement) => {
        setPlacements((prev) => ({ ...prev, [tunnelId]: placement }));
        return placement;
      })
      .catch(() => undefined);

  // A placement only exists for a tunnel that is based AND carries lugs. Legacy
  // holds a LugCollection model for based tunnels only and routes every refetch
  // through `refetchModels`, which returns early with no model
  // (collection/tunnel.lugs.js:33-35); the lug half is ours — with the Base a
  // tunnel has nothing else the response feeds, so there is nothing to GET.
  // `lugsOnForTunnel`, not `t.lug`: on a hybrid AHU that column is false on
  // every tunnel (the flag is the unit's), so this answered false for all of
  // them and the pane fetched nothing at all.
  const tunnelHasLugs = (tunnelId: number) =>
    tunnels.some((t) => t.id === tunnelId && t.base_id != null && lugsOnForTunnel(ahu, t));

  const refetchPlacement = (tunnelId: number) =>
    tunnelHasLugs(tunnelId) ? fetchPlacement(tunnelId) : Promise.resolve(undefined);

  // `model.hide_lugs = true` (tab.lifting.lug:513). The canvas reads the query
  // cache, so writing the flag there is what legacy's single shared collection
  // gave for free: `redrawTunnelLugs` clears the tunnel's lugs and then bails on
  // the flag instead of drawing the stale ones back. The next successful
  // refetch replaces the entry, flag and all, which is legacy's `hide_lugs =
  // false` on refetchModels (collection/tunnel.lugs.js:36).
  const markPlacementStale = (tunnelId: number) =>
    dispatch(
      ahuBoardApi.util.updateQueryData('getTunnelLugPlacement', tunnelId, (draft) => {
        draft.hide_lugs = true;
      })
    );

  // Create-or-refetch, on the same fork legacy takes after a tunnel save
  // (tunnel.js:311-317): no model held for this tunnel → `createModel`, which
  // POSTs and keeps the response; a model already held → `refetchModels`, which
  // GETs. So turning lugs on for the first time creates the record rather than
  // GETting one that isn't there — React never ported either of legacy's two
  // creation points (that POST, and the auto-create ridden in on
  // GET ahu/tunnels/<id>/, tunnel_viewset.py:130), which is why these tunnels
  // have no AHULug row at all. POST is idempotent server-side
  // (AHULugSerializer.create reuses an existing row), so a stale local miss
  // costs a redundant write, never a duplicate.
  const ensurePlacement = (tunnelId: number) =>
    placementsRef.current[tunnelId]
      ? fetchPlacement(tunnelId)
      : createTunnelLug({ tunnel_id: tunnelId })
          .unwrap()
          .then((created) => {
            setPlacements((prev) => ({ ...prev, [tunnelId]: created }));
            return created;
          })
          .catch(() => undefined);

  // Initial placements (one per lug-bearing tunnel), deferred to the first open:
  // the pane stays mounted while Base & Lugs is collapsed, and nothing it renders
  // in that state reads a placement. Unforced, so it reuses whatever the board
  // bootstrap already cached rather than re-requesting it.
  const lugTunnelIds = basedTunnels.filter((t) => lugsOnForTunnel(ahu, t)).map((t) => t.id);
  useEffect(() => {
    if (!open) return;
    lugTunnelIds.forEach((id) => {
      if (!placementsRef.current[id]) void fetchPlacement(id, false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lugTunnelIds.join(',')]);

  // `tunnelLugs:recalcPlacement` / `recalc:lug:placement` / `render_tunnel_lugs` (§7.3.2).
  useEffect(() => {
    const offs = [
      ahuBoardBus.on('tunnelLugs:recalcPlacement', ({ tunnel_id }) => {
        // addTunnelIdForRecalc:509 — `if (!this.models[tunnel_id]) return`. The
        // emitters (section/accessory/dmount forms) fire for any tunnel, so one
        // with no lugs would otherwise arm ReCalculate and GET on the way out.
        if (!tunnelHasLugs(tunnel_id)) return;
        setPendingRecalc((prev) => ({ ...prev, [tunnel_id]: true }));
        // Arming the button means the drawn lugs no longer match the tunnel, so
        // they come off the canvas now. Legacy stamped `hide_lugs` on the shared
        // model here (:513) but only redrew when the caller passed
        // `hide_from_canvas`, which left stale lugs on screen for every other
        // emitter; we drop that condition and always take them down.
        markPlacementStale(tunnel_id);
        ahuBoardBus.emit('redraw:tunnel:lugs', tunnel_id, true);
      }),
      ahuBoardBus.on('recalc:lug:placement', () => {
        setRecalcRunning(true);
        const ids = Object.keys(pendingRecalc).map(Number);
        Promise.all(ids.map((id) => refetchPlacement(id)))
          .then(() => {
            setPendingRecalc({});
            setRecalcRunning(false);
            ids.forEach((id) => ahuBoardBus.emit('redraw:tunnel:lugs', id));
          })
          .catch(() => setRecalcRunning(false));
      }),
      // A placement re-read somewhere else in the board (the section-insert
      // path's AHU-wide refetch, and the lug POST behind a new tunnel) lands
      // in the query cache the canvas reads. This mirror is the pane's half of
      // what legacy held in one shared LugCollection, so take the same body —
      // otherwise everything the placement feeds stays at whatever was true
      // when this tunnel was first read, `valid_kinds` (the Customize Lugs
      // kind picker) included, until a reload re-seeds it from the boot GET.
      ahuBoardBus.on('tunnelLugs:placementRefetched', (tunnelId, placement) => {
        setPlacements((prev) => ({
          ...prev,
          [tunnelId]: placement as unknown as IAhuTunnelLugPlacement,
        }));
      }),
      ahuBoardBus.on('render_tunnel_lugs', () => {
        // post tunnel delete — re-sync tunnels list (props already fresh).
        setPlacements((prev) => {
          const next: Record<number, IAhuTunnelLugPlacement> = {};
          basedTunnels.forEach((t) => {
            if (prev[t.id]) next[t.id] = prev[t.id];
          });
          return next;
        });
      }),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRecalc, basedTunnels]);

  // ReCalculate button arming (§7.3.2 / parity 126). `recalculate-lug-placement`
  // is what positions it: `.base-lugs-panel .recalculate-lug-placement` pins it
  // to the collapse header (top 6px / right 40px). Without the class it drops
  // back into the flow as a plain block under the list.
  const recalcArmed = Object.keys(pendingRecalc).length > 0;
  const recalcButton = (
    <button
      id="recalculate_lug_placement"
      className={`btn recalculate-lug-placement${recalcArmed ? ' warning' : ' non-clickable'}`}
      onClick={() => {
        if (recalcArmed) ahuBoardBus.emit('recalc:lug:placement');
      }}
    >
      <div className={`preloader dotted-preloader${recalcRunning ? ' active' : ''}`} />
      ReCalculate tunnel Lug Placement
    </button>
  );

  if (!open) {
    return (
      <>
        <ul id="lifting-lug-wrapper-tab" className="sublist lifting-lug-wrapper-tab">
          <li className="spinner-wrapper small">
            <div className="preloader dotted-preloader active" />
          </li>
        </ul>
        {recalcButton}
      </>
    );
  }

  const placement = activeTunnelId != null ? placements[activeTunnelId] : undefined;
  const positions = (placement?.positions ?? []) as LugPosition[];
  const lugModels = ((placement as Record<string, unknown> | undefined)?.models ??
    []) as Array<Record<string, unknown>>;
  const lugBaseDirections = ((placement as Record<string, unknown> | undefined)
    ?.lug_base_direction ?? []) as Array<Record<string, unknown>>;
  const paramsData = (lugParams ?? {}) as {
    tunnel?: { base_direction?: Array<Record<string, unknown>>; to_avoid_lug?: unknown[] };
  };
  const tunnelBaseDirections = paramsData.tunnel?.base_direction ?? [];

  // Which placement the canvas selection is resolved against. Legacy looks
  // each active lug up in `this.models[l.tunnel_id]` (tab.lifting.lug:154) —
  // the placement of the tunnel the lug was CLICKED on, not of the active one.
  // Non-hybrid the two agree (the pill follows the pick, above) and legacy then
  // drops a selection whose tunnel isn't the active one (:568), which reading
  // the active tunnel's positions reproduces exactly — so that path is left
  // alone. Hybrid has no pill: the active tunnel follows the AHU-wide section
  // pick, and a base group's lug can sit on a tunnel that isn't the selected
  // one, which is why legacy shows the selection regardless of tunnel there
  // (`is_hybrid_ahu ||`, :568). Without this, clicking a lug on any section
  // other than the selected one selected and tinted the lug on canvas but left
  // the Data Bar empty.
  const lugTunnelId = isHybrid ? activeLugTunnelId ?? activeTunnelId : activeTunnelId;
  const lugPositions =
    lugTunnelId === activeTunnelId
      ? positions
      : ((lugTunnelId != null ? placements[lugTunnelId]?.positions : undefined) ??
          []) as LugPosition[];
  const activeLugPositions = lugPositions.filter((p) =>
    activeLugIds.some((id) => String(id) === String(p.id))
  );
  const activeLug: LugPosition | null =
    activeLugPositions.length > 1
      ? mergeLugs(activeLugPositions)
      : activeLugPositions[0] ?? null;
  // The X/Y inputs below are uncontrolled (`defaultValue` + onBlur, so typing
  // isn't fought by a re-render), so React keeps whatever is in the DOM when
  // the surrounding rows re-render — selecting a second lug left the first
  // one's numbers on screen. Remount the block whenever the selected row (or
  // its saved values) actually change; legacy re-rendered the whole template
  // on both.
  const activeLugKey = activeLugPositions
    .map((p) => `${p.id}:${p.x}:${p.y}:${p.model}`)
    .join(',');
  const emptyOptionName = activeLugPositions.length > 1 ? 'varies...' : '- -';
  const lastTunnel = tunnels[tunnels.length - 1];
  const showHint = (() => {
    const idx = String(lastTunnel?.index ?? '').split(':').map(Number);
    return idx.length === 2 && idx[0] > idx[1];
  })();

  // Hybrid AHUs configure lugs at the AHU level (ahu.lug/lug_defaults), not
  // per-tunnel — sync_hybrid_lugs reads these off the AHU, not the tunnel.
  const defaultsOn = isHybrid ? !!ahu.lug_defaults : !!activeTunnel?.tunnel_lug_defaults;
  const lugOn = isHybrid ? !!ahu.lug : !!activeTunnel?.lug;
  // Legacy gated Include Lifting Lugs on the placement's `models` list
  // (tab.lifting.lug:324), which greys the checkbox out for any tunnel whose
  // placement 404s — locking lugs OFF on exactly the tunnels that have none
  // yet, the state you need it in to turn them on. `has_lug_models` asks the
  // same question of the tunnel rather than the placement, so it answers for a
  // tunnel with no AHULug row: false with no Base, otherwise whether any lug
  // model fits that Base. The placement's list still fills the Model select.
  // Hybrid: the AHU-wide checkbox isn't tied to one tunnel's catalog fit.
  const disableLug = isHybrid ? false : !activeTunnel?.has_lug_models;
  const validKinds = (placement?.valid_kinds ?? []) as string[];
  // Legacy's kind select has no placeholder option and marks the first kind
  // `selected`, so Add always has something to send. Mirror that rather than
  // starting on an empty value that disables the button — but fall back again
  // if the tunnel changes under a kind that is no longer valid for it.
  const addLugKindValue =
    addLugKind && validKinds.includes(addLugKind) ? addLugKind : validKinds[0] ?? '';
  // Legacy's `hasModelDetails` (tab.lifting.lug:514) is
  // `this.models[activeTunnelId]?.get("models")` — the PLACEMENT's lug-model
  // catalog, i.e. "has this tunnel's placement been fetched". Asking instead
  // whether the existing positions carry model details made Add require lugs
  // to already be there, which is exactly the state it exists to get you out
  // of: a section added just now has none, so the button that would give it
  // some was the one thing hidden. `add_manual_lug_point` creates the AHULug
  // row itself (base_group_lug.py:455), so Add works from zero.
  const hasModelDetails = (placement as Record<string, unknown> | undefined)?.models != null;
  const showHybridAddControls =
    activeTunnelId != null && lugOn && validKinds.length > 0 && hasModelDetails;

  // Tunnel-settings change → PATCH tunnel → refetch lugs → re-render (§7.3.2).
  const changeTunnelSetting = (name: string, value: unknown) => {
    if (activeTunnelId == null || !activeTunnel) return;
    // Clearing the Base — or unticking Include Lifting Lugs — drops the placement
    // rather than refetching it (legacy tunnel.js:311-313 → LugCollection.removeModel):
    // the tunnel has no lugs left to place, so there is nothing to GET. Decide off
    // the post-PATCH value; the `tunnels` prop is still the pre-PATCH one here.
    const keepsLugs =
      !!(name === 'base_id' ? value : activeTunnel.base_id) &&
      !!(name === 'lug' ? value : activeTunnel.lug);
    setRecalcRunning(true);
    patchTunnel({ id: activeTunnelId, ahuId: activeTunnel.ahu_id, data: { [name]: value } })
      .unwrap()
      .then(() => {
        if (keepsLugs) return ensurePlacement(activeTunnelId);
        setPlacements((prev) => {
          const next = { ...prev };
          delete next[activeTunnelId];
          return next;
        });
        return undefined;
      })
      .then(() => {
        setRecalcRunning(false);
        ahuBoardBus.emit('redraw:tunnel:lugs', activeTunnelId, !keepsLugs);
      })
      .catch(() => setRecalcRunning(false));
  };

  // AHU-level lug settings (hybrid only — sync_hybrid_lugs reads these off
  // the AHU, not the tunnel). A single save fans out re-fetches to every
  // based tunnel, not just the active one, since the setting is AHU-wide.
  const changeAhuLugSetting = (name: string, value: unknown) => {
    setRecalcRunning(true);
    patchAhuUnit({ id: ahu.id, data: { [name]: value } })
      .unwrap()
      .then(() =>
        Promise.all(basedTunnels.filter((t) => lugsOnForTunnel(ahu, t)).map((t) => fetchPlacement(t.id)))
      )
      .then(() => {
        setRecalcRunning(false);
        if (activeTunnelId != null) ahuBoardBus.emit('redraw:tunnel:lugs', activeTunnelId, true);
      })
      .catch(() => setRecalcRunning(false));
  };

  // Per-lug change — PATCH each active lug setting + redraw (§7.3.2). Keyed on
  // the lug's OWN tunnel, not the active one (legacy redraws with `lug.tunnel`,
  // tab.lifting.lug:391): on a hybrid AHU the selected lug can belong to a
  // different tunnel than the selected section, and both the placement entry
  // this updates and the tunnel the canvas redraws have to be that one.
  const changeLugField = (name: string, value: unknown) => {
    if (lugTunnelId == null) return;
    activeLugPositions.forEach((lug) => {
      const lugId = Number(lug.id);
      if (!Number.isFinite(lugId)) return;
      patchLugSetting({ id: lugId, data: { [name]: value } })
        .unwrap()
        // Take the whole saved row, not `{[name]: value}`. One field can carry
        // others with it -- Model brings its own `attachment_name` and
        // `model_details` -- and merging only what we sent left the readouts
        // showing the previous model's values. It also matters to the canvas:
        // `drawLug` dispatches on `lug.model_details.style`, so a partial row
        // has no style to draw with.
        .then((saved) => {
          const row = { ...lug, ...(saved as LugPosition) };
          setPlacements((prev) => {
            const cur = prev[lugTunnelId];
            if (!cur) return prev;
            const nextPositions = ((cur.positions ?? []) as LugPosition[]).map((p) =>
              String(p.id) === String(lugId) ? row : p
            );
            return { ...prev, [lugTunnelId]: { ...cur, positions: nextPositions } };
          });
          ahuBoardBus.emit('redraw:tunnel:lug', lugTunnelId, row);
        })
        .catch(() => undefined);
    });
  };

  // `kind` (left/right/upstream/downstream) is hybrid-only — which Base Group
  // edge the manual add targets (§4/AddLugKind picker below).
  const addLug = (kind?: string) => {
    if (activeTunnelId == null) return;
    createLugSetting({ tunnel_id: activeTunnelId, ...(kind ? { kind } : {}) })
      .unwrap()
      .then((newLugs) => {
        const rows = (Array.isArray(newLugs) ? newLugs : [newLugs]) as LugPosition[];
        setPlacements((prev) => {
          const cur = prev[activeTunnelId];
          if (!cur) return prev;
          return {
            ...prev,
            [activeTunnelId]: {
              ...cur,
              positions: [...((cur.positions ?? []) as LugPosition[]), ...rows],
            },
          };
        });
        rows.forEach((lug) => ahuBoardBus.emit('redraw:tunnel:lug', activeTunnelId, lug));
        // Select what was just added: the canvas tints it and answers with
        // `change:active:lugs`, which is what fills the Lug Settings fields
        // below — so Add leaves you on the new lug instead of on nothing.
        // Emitted after the draws; the lug-style builders call back
        // synchronously, so the shapes are registered by now.
        ahuBoardBus.emit(
          'select:tunnel:lugs',
          rows.map((lug) => lug.id as number | string)
        );
      })
      .catch(() => undefined);
  };

  const deleteActiveLugs = () => {
    if (lugTunnelId == null) return;
    Promise.all(
      activeLugPositions.map((lug) =>
        deleteLugSetting(Number(lug.id))
          .unwrap()
          .then(() => {
            ahuBoardBus.emit('redraw:tunnel:lug', lugTunnelId, { id: lug.id }, true);
          })
          .catch(() => undefined)
      )
    ).then(() => {
      setPlacements((prev) => {
        const cur = prev[lugTunnelId];
        if (!cur) return prev;
        return {
          ...prev,
          [lugTunnelId]: {
            ...cur,
            positions: ((cur.positions ?? []) as LugPosition[]).filter(
              (p) => !activeLugPositions.some((a) => String(a.id) === String(p.id))
            ),
          },
        };
      });
      dispatch(setActiveLugs({ ids: [], tunnelId: lugTunnelId }));
      setDeletePopup(false);
    });
  };

  const confirmDefaults = (mode: 'check' | 'uncheck') => {
    const cfg = LUG_DEFAULTS_COPY[mode];
    if (activeTunnelId == null || !activeTunnel) return;
    // CHECK/UNCHECK dismisses the popup on the click. The PATCH and the refetch
    // behind it are reported by the pane spinner and the one on the ReCalculate
    // button — legacy raised both before opening the popup
    // (tab.lifting.lug:131-133) but only called popup.remove() once the save had
    // come back, leaving a dead popup sitting over its own progress.
    setDefaultsPopup(null);
    setRecalcRunning(true);
    const save = isHybrid
      ? patchAhuUnit({ id: ahu.id, data: { lug_defaults: cfg.newValue } }).unwrap()
      : patchTunnel({
          id: activeTunnelId,
          ahuId: activeTunnel.ahu_id,
          data: { tunnel_lug_defaults: cfg.newValue },
        }).unwrap();
    save
      .then(() => {
        if (cfg.newValue) {
          const refetch = isHybrid
            ? Promise.all(basedTunnels.filter((t) => lugsOnForTunnel(ahu, t)).map((t) => fetchPlacement(t.id)))
            : refetchPlacement(activeTunnelId);
          return refetch.then(() => {
            dispatch(setActiveLugs({ ids: [], tunnelId: activeTunnelId }));
            setPendingRecalc((prev) => {
              const next = { ...prev };
              delete next[activeTunnelId];
              return next;
            });
            return undefined;
          });
        }
        return undefined;
      })
      .then(() => {
        ahuBoardBus.emit('redraw:tunnel:lugs', activeTunnelId, true);
        setRecalcRunning(false);
      })
      .catch(() => setRecalcRunning(false));
  };

  // Rows except .always-visible grey while defaults on (checkIsChangable).
  const lockedClass = (always: boolean) =>
    !always && defaultsOn ? ' non-clickable' : '';

  const num2 = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toFixed(1) : '';
  };

  if (isHybrid) {
    return (
      <>
        <ul id="lifting-lug-wrapper-tab" className="sublist lifting-lug-wrapper-tab">
          <li className={`spinner-wrapper${recalcRunning ? '' : ' hide'}`}>
            <div className="preloader dotted-preloader active" />
          </li>
          {/* tab.lifting.lug.tmpl:9-56 — the four AHU-wide settings, rendered
              unconditionally (NOT inside the `lug`/`base_id` gate the per-lug
              block below sits in). Legacy greys them via `non-clickable` on the
              inner div while Include Lifting Lugs is off; it does not drop them
              from the list, and it has no "AHU Lug Settings"/"Placement
              Settings" headings on this path — both of those are
              `{{#unless is_hybrid}}`-only. */}
          <li className="always-visible">
            <div className={disableLug ? 'non-clickable' : ''}>
              <input
                type="checkbox"
                className="ahu_field"
                name="lug"
                checked={lugOn}
                onChange={(e) => changeAhuLugSetting('lug', e.currentTarget.checked)}
              />
              <span className="label">
                <span>Include Lifting Lugs</span>
              </span>
            </div>
          </li>
          <li className="always-visible">
            <div className={lugOn ? '' : 'non-clickable'}>
              <input
                type="checkbox"
                className="ahu_field"
                name="lug_defaults"
                checked={defaultsOn}
                onChange={(e) => setDefaultsPopup(e.currentTarget.checked ? 'check' : 'uncheck')}
              />
              <span className="label">
                <span>Placement &amp; Lug Defaults</span>
              </span>
            </div>
          </li>
          {defaultsOn && (
            <li className={lockedClass(false)}>
              <div className={`single-line${lugOn ? '' : ' non-clickable'}`}>
                <select
                  className="ahu_field"
                  name="base_direction_id"
                  value={ahu.base_direction_id ?? ''}
                  onChange={(e) =>
                    changeAhuLugSetting(
                      'base_direction_id',
                      e.currentTarget.value ? Number(e.currentTarget.value) : null
                    )
                  }
                >
                  <option className="hide" value="">
                    - -
                  </option>
                  {tunnelBaseDirections.map((d) => (
                    <option key={Number(d.id)} value={Number(d.id)}>
                      {String(d.name ?? '')}
                    </option>
                  ))}
                </select>
                <span className="label">
                  <span>Base Direction</span>
                </span>
              </div>
            </li>
          )}
          <li className={lockedClass(false)}>
            <div className={lugOn ? '' : 'non-clickable'}>
              <input
                type="checkbox"
                className="ahu_field"
                name="lugs_on_split_line_base"
                checked={!!ahu.lugs_on_split_line_base}
                onChange={(e) =>
                  changeAhuLugSetting('lugs_on_split_line_base', e.currentTarget.checked)
                }
              />
              <span className="label">
                <span>Lugs on split-line bases</span>
              </span>
            </div>
          </li>
          {/* `before-lug-settings` shortens this one spacer so the Customize
              Lugs row sits closer to the settings above it (requested
              2026-09-30). The class is on the spacer rather than on
              `.empty-row` itself because the pane uses four more of them. */}
          <li className={`empty-row before-lug-settings${lockedClass(false)}`} />
          {/* tab.lifting.lug.tmpl:96/107 — `{{#if base_id}}{{#if lug}}`: the
              per-lug half needs the active tunnel to actually have a Base. */}
          {activeTunnel?.base_id != null && lugOn && (
            <>
              <li className={lockedClass(false)}>
                <div className="lug-settings-row">
                  {/* Legacy swapped Add out for the trash as soon as a lug was
                      selected, and `addLug` selects what it just created — so
                      adding a lug hid the button you add lugs with, and a
                      second one needed a click in the canvas first. Both live
                      side by side instead: the kind picker and Add stay put,
                      Delete joins them only while a lug is selected. */}
                  <div className="buttons-wrapper">
                    {showHybridAddControls && (
                      <>
                        {/* Legacy has no "- -" option and pre-selects the
                            first kind (`{{#if @first}}selected{{/if}}`), so
                            Add is always live. `add-lug-kind` is what sizes
                            it to its content and title-cases the raw kind. */}
                        <select
                          id="add-lug-kind"
                          className="add-lug-kind"
                          value={addLugKindValue}
                          onChange={(e) => setAddLugKind(e.currentTarget.value)}
                        >
                          {validKinds.map((k) => (
                            <option key={k} value={k}>
                              {k}
                            </option>
                          ))}
                        </select>
                        <button
                          id="add-lug"
                          className="ka-has-tooltip"
                          {...{ tooltip: 'Add Lug' }}
                          onClick={() => addLug(addLugKindValue)}
                        >
                          <span>+</span> Add
                        </button>
                      </>
                    )}
                    {activeLug && (
                      <button
                        id="delete-lug"
                        className="icon-trash ka-has-tooltip"
                        {...{ tooltip: 'Delete Lug' }}
                        onClick={() => setDeletePopup(true)}
                      />
                    )}
                  </div>
                  <span className="label bold">
                    <span>Customize Lugs</span>
                  </span>
                </div>
              </li>
              <li className={`hint-row${lockedClass(false)}`}>
                <div className="">
                  <span className="label">
                    <span>To customize the lugs in one or more Base Groups:</span>
                    <span>
                      Uncheck &quot;Placement &amp; Lug Defaults&quot; under &quot;AHU Lug
                      Settings&quot; in the AHU tab.
                    </span>
                    <span>
                      To select a lug to move or delete, click on the lug in the 3D Canvas.
                    </span>
                    <span>
                      Press and hold the CTRL key and click on lugs, to select two or more lugs
                      simultaneously.
                    </span>
                  </span>
                </div>
              </li>
              {activeLug && (
                <Fragment key={activeLugKey}>
                  <li className={lockedClass(false)}>
                    <div className="single-line">
                      {/* tab.lifting.lug.tmpl:230-245 — with no model catalog to
                          build a select from, legacy falls back to the lug's own
                          `model_name` as a read-only `.selection` plus an empty
                          `<select>` stub, the same shape as Attachment below, so
                          the row still lines up with the selects around it. The
                          stub is a `.readonly-field-box` here rather than a real
                          `<select>` — see ahuBoardOverrides.scss. */}
                      {lugModels.length ? (
                        <select
                          className="tunnel_lugs_field"
                          name="model"
                          value={activeLug.model == null ? '' : String(activeLug.model)}
                          onChange={(e) =>
                            changeLugField(
                              'model',
                              e.currentTarget.value ? Number(e.currentTarget.value) : null
                            )
                          }
                        >
                          <option className="hide" value="">
                            {emptyOptionName}
                          </option>
                          {lugModels.map((m) => (
                            <option key={Number(m.id)} value={Number(m.id)}>
                              {String(m.model ?? '')}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <span className="selection">
                            <span>{String(activeLug.model_name ?? '')}</span>
                          </span>
                          <span className="readonly-field-box" aria-hidden="true" />
                        </>
                      )}
                      <span className="label">
                        <span>Model</span>
                      </span>
                    </div>
                  </li>
                  {/* Read-only output — no catalog to pick from, so no control.
                      Legacy parks an empty `<select></select>` here purely to
                      give the row a box; that draws a dropdown caret on a field
                      with nothing behind it, which is the reported bug. The
                      `.readonly-field-box` span is that same box without the
                      caret (ahuBoardOverrides.scss). */}
                  <li className={lockedClass(false)}>
                    <div className="single-line">
                      <span className="selection">
                        <span>{String(activeLug.attachment_name ?? '')}</span>
                      </span>
                      <span className="readonly-field-box" aria-hidden="true" />
                      <span className="label">
                        <span>Attachment</span>
                      </span>
                    </div>
                  </li>
                  {/* "Base Direction" renamed "Location" for hybrid — disambiguates
                      from the AHU-level "Base Direction" setting above. */}
                  <li className={lockedClass(false)}>
                    <div className="single-line">
                      {/* tab.lifting.lug.tmpl:264-280 — same read-only fallback as
                          Model: the hybrid tunnel_lugs response carries no
                          `lug_base_direction` catalog, so legacy shows the lug's
                          own `base_direction_name` instead of an empty select. */}
                      {lugBaseDirections.length ? (
                        <select
                          className="tunnel_lugs_field"
                          name="base_direction"
                          value={
                            activeLug.base_direction == null
                              ? ''
                              : String(activeLug.base_direction)
                          }
                          onChange={(e) =>
                            changeLugField(
                              'base_direction',
                              e.currentTarget.value ? Number(e.currentTarget.value) : null
                            )
                          }
                        >
                          <option className="hide" value="">
                            {emptyOptionName}
                          </option>
                          {lugBaseDirections.map((d) => (
                            <option key={Number(d.id)} value={Number(d.id)}>
                              {String(d.name ?? '')}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <span className="selection">
                            <span>{String(activeLug.base_direction_name ?? '')}</span>
                          </span>
                          <span className="readonly-field-box" aria-hidden="true" />
                        </>
                      )}
                      <span className="label">
                        <span>Location</span>
                      </span>
                    </div>
                  </li>
                  <li className={lockedClass(false)}>
                    <div className="">
                      <span className="unit"> in</span>
                      <input
                        readOnly={activeLug.base_direction === 2}
                        disabled={activeLug.base_direction === 2}
                        data-type="number"
                        data-step="0.01"
                        className="tunnel_lugs_field"
                        name="x"
                        defaultValue={activeLug.x == null ? '' : String(activeLug.x)}
                        placeholder="varies..."
                        onBlur={(e) => {
                          const v = e.currentTarget.value;
                          if (v !== '') changeLugField('x', Number(v));
                        }}
                      />
                      <span className="label">
                        <span>X (tunnel length)</span>
                      </span>
                    </div>
                  </li>
                  <li className={lockedClass(false)}>
                    <div className="">
                      <span className="unit"> in</span>
                      <input
                        readOnly={activeLug.base_direction === 1}
                        disabled={activeLug.base_direction === 1}
                        data-type="number"
                        data-step="0.01"
                        className="tunnel_lugs_field"
                        name="y"
                        defaultValue={activeLug.y == null ? '' : String(activeLug.y)}
                        placeholder="varies..."
                        onBlur={(e) => {
                          const v = e.currentTarget.value;
                          if (v !== '') changeLugField('y', Number(v));
                        }}
                      />
                      <span className="label">
                        <span>Y (tunnel width)</span>
                      </span>
                    </div>
                  </li>
                  {showHint && (
                    <li className={lockedClass(false)}>
                      <div className="brake-line">
                        <span className="label">
                          <span>
                            In order to view the lugs on the adjoining base(s) of a tunnel,
                            select a specific tunnel.
                          </span>
                        </span>
                      </div>
                    </li>
                  )}
                  <li className={`empty-row${lockedClass(false)}`} />
                  <li
                    className="toggle-cms-info always-visible"
                    onClick={() => setShowCmsInfo((v) => !v)}
                  >
                    <div className="">
                      <span className="label bold">
                        <span>Manufacturer&apos;s lug placement standards ...</span>
                      </span>
                    </div>
                  </li>
                  {showCmsInfo && (
                    <>
                      <li className="cms-info-item always-visible">
                        <div className="">
                          <span className="selection">
                            <span>{num2(activeLug.min_dist_from_base_edge)}</span>
                          </span>
                          <span className="label">Minimum Distance from base edge</span>
                          <span className="unit">in</span>
                        </div>
                      </li>
                      <li className="cms-info-item always-visible">
                        <div className="">
                          <span className="selection">
                            <span>{num2(activeLug.max_center_spacing)}</span>
                          </span>
                          <span className="label">Maximum lug center spacing</span>
                          <span className="unit">in</span>
                        </div>
                      </li>
                      <li className="cms-info-item always-visible">
                        <div className="">
                          <span className="selection">
                            <span>{String(activeLug.max_weight_per_lug ?? '')}</span>
                          </span>
                          <span className="label">Max weight per lug</span>
                          <span className="unit">lb</span>
                        </div>
                      </li>
                    </>
                  )}
                </Fragment>
              )}
            </>
          )}
        </ul>
        {recalcButton}
        {defaultsPopup && (
          <UniversalPopup
            title={LUG_DEFAULTS_COPY[defaultsPopup].title}
            content={LUG_DEFAULTS_COPY[defaultsPopup].content}
            actionText={LUG_DEFAULTS_COPY[defaultsPopup].actionText}
            cancelText="CANCEL"
            onAction={() => confirmDefaults(defaultsPopup)}
            onCancel={() => setDefaultsPopup(null)}
          />
        )}
        {deletePopup && (
          <DeleteConfirmPopup
            title="Delete Lug?"
            content="If you choose to Delete, the lug and associated data will be deleted and will not be recoverable."
            onDelete={deleteActiveLugs}
            onCancel={() => setDeletePopup(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <ul id="lifting-lug-wrapper-tab" className="sublist lifting-lug-wrapper-tab">
        <li className={`spinner-wrapper${recalcRunning ? '' : ' hide'}`}>
          <div className="preloader dotted-preloader active" />
        </li>
        <li className="always-visible tunnels-row">
          <div className="single-line">
            <div className="tunnels-indexes">
              <div className="tunnels">
                {visibleTunnels.map((t) => (
                  <span
                    key={t.id}
                    className={`tunnel-index${t.id === activeTunnelId ? ' active' : ''}`}
                    data-id={t.id}
                    onClick={() => setPillActiveTunnelId(t.id)}
                  >
                    {t.index}
                  </span>
                ))}
              </div>
            </div>
            <div className={`tunnels-indexes-nav${tunnels.length > maxVisibleTunnels ? '' : ' hide'}`}>
              <label
                className={`icon-chevron-left tunnels-indexes-arrow tunnels-indexes-arrow-left${canNavTunnelsLeft ? '' : ' disabled'}`}
                onClick={() => canNavTunnelsLeft && shiftVisibleTunnels(-1)}
              />
              <label
                className={`icon-chevron-right tunnels-indexes-arrow tunnels-indexes-arrow-right${canNavTunnelsRight ? '' : ' disabled'}`}
                onClick={() => canNavTunnelsRight && shiftVisibleTunnels(1)}
              />
            </div>
            <span className="label">
              <span> Tunnel</span>
            </span>
          </div>
        </li>

        <li className="always-visible">
          <div className="single-line">
            <select
              name="base_id"
              data-tunnel={activeTunnel?.id}
              required
              className="tunnel_settings_field"
              value={activeTunnel?.base_id ?? ''}
              onChange={(e) =>
                changeTunnelSetting('base_id', e.currentTarget.value || null)
              }
            >
              <option value="">- -</option>
              {cmsBaseList(cms).map((b) => (
                <option key={b.id} value={b.id}>
                  {catalogLabel(b)}
                </option>
              ))}
            </select>
            <span className="label">Base</span>
          </div>
        </li>

        {activeTunnel?.base_id != null && (
          <>
            <li className="always-visible">
              <div className={disableLug ? 'non-clickable' : ''}>
                <input
                  type="checkbox"
                  className="tunnel_settings_field"
                  name="lug"
                  checked={lugOn}
                  onChange={(e) => changeTunnelSetting('lug', e.currentTarget.checked)}
                />
                <span className="label">
                  <span>Include Lifting Lugs</span>
                </span>
              </div>
            </li>
            {lugOn && (
              <>
                <li className="always-visible">
                  <div className="">
                    <input
                      type="checkbox"
                      className="tunnel_settings_field"
                      name="tunnel_lug_defaults"
                      checked={defaultsOn}
                      onChange={(e) => {
                        // Revert-then-confirm semantics (§7.3.2): the popup
                        // owns the final state; checked state stays put until
                        // CONFIRM (controlled input = automatic revert).
                        setDefaultsPopup(e.currentTarget.checked ? 'check' : 'uncheck');
                      }}
                    />
                    <span className="label">
                      <span>Placement &amp; Lug Defaults</span>
                    </span>
                  </div>
                </li>
                <li className="empty-row" />
                <li className={lockedClass(false)}>
                  <div className="">
                    <span className="label bold">
                      <span>Placement Settings</span>
                    </span>
                  </div>
                </li>
                <li className={lockedClass(false)}>
                  <div className="single-line">
                    <select
                      className="tunnel_settings_field"
                      name="base_direction_id"
                      value={activeTunnel?.base_direction_id ?? ''}
                      onChange={(e) =>
                        changeTunnelSetting(
                          'base_direction_id',
                          e.currentTarget.value ? Number(e.currentTarget.value) : null
                        )
                      }
                    >
                      <option className="hide" value="">
                        - -
                      </option>
                      {tunnelBaseDirections.map((d) => (
                        <option key={Number(d.id)} value={Number(d.id)}>
                          {String(d.name ?? '')}
                        </option>
                      ))}
                    </select>
                    <span className="label">
                      <span>Base Direction</span>
                    </span>
                  </div>
                </li>
                <li className={lockedClass(false)}>
                  <div>
                    <input
                      type="checkbox"
                      className="tunnel_settings_field"
                      name="lugs_on_tunnel_adjoining_base"
                      checked={!!activeTunnel?.lugs_on_tunnel_adjoining_base}
                      onChange={(e) =>
                        changeTunnelSetting(
                          'lugs_on_tunnel_adjoining_base',
                          e.currentTarget.checked
                        )
                      }
                    />
                    {!!activeTunnel?.lugs_on_tunnel_adjoining_base && (
                      <div className="d-inline non-clickable">
                        <label>
                          <input
                            type="checkbox"
                            className="tunnel_settings_field hide"
                            name="show"
                            readOnly
                            checked={!!(activeTunnel as Record<string, unknown>).show}
                          />
                          <span className="icon-eye">show in model</span>
                        </label>
                      </div>
                    )}
                    <span className="label">
                      <span>Lugs on adjoining tunnel bases</span>
                    </span>
                  </div>
                </li>
                <li className={lockedClass(false)}>
                  <div className="">
                    <input
                      type="checkbox"
                      className="tunnel_settings_field"
                      name="lugs_on_split_line_base"
                      checked={!!activeTunnel?.lugs_on_split_line_base}
                      onChange={(e) =>
                        changeTunnelSetting('lugs_on_split_line_base', e.currentTarget.checked)
                      }
                    />
                    <span className="label">
                      <span>Lugs on split-line bases</span>
                    </span>
                  </div>
                </li>
                {/* Shortened spacer — see the hybrid row above. */}
                <li className="empty-row before-lug-settings" />
                <li className={lockedClass(false)}>
                  <div className="lug-settings-row">
                    {/* Add stays put once a lug is selected (see the hybrid
                        row above); Delete joins it rather than replacing it. */}
                    <div className="buttons-wrapper">
                      <button
                        id="add-lug"
                        className="ka-has-tooltip"
                        {...{ tooltip: 'Add Lug' }}
                        onClick={() => addLug()}
                      >
                        <span>+</span> Add
                      </button>
                      {activeLug && (
                        <button
                          id="delete-lug"
                          className="icon-trash ka-has-tooltip"
                          {...{ tooltip: 'Delete Lug' }}
                          onClick={() => setDeletePopup(true)}
                        />
                      )}
                    </div>
                    {/* KA-x 74853f97b8 renamed this in the ONE legacy
                        template, so it reads "Customize Lugs" on a standard
                        AHU as well — only the hybrid half of the port picked
                        the new wording up. */}
                    <span className="label bold">
                      <span>Customize Lugs</span>
                    </span>
                  </div>
                </li>
                <li className={`hint-row${lockedClass(false)}`}>
                  <div className="">
                    <span className="label">
                      <span>To select a lug, click on it in the 3D model</span>
                      <span>
                        Press and hold the CTRL key, in order to select two lugs
                        simultaneously.
                      </span>
                    </span>
                  </div>
                </li>
                {activeLug && (
                  <Fragment key={activeLugKey}>
                    <li className={lockedClass(false)}>
                      <div className="single-line">
                        <select
                          className="tunnel_lugs_field"
                          name="model"
                          value={activeLug.model == null ? '' : String(activeLug.model)}
                          onChange={(e) =>
                            changeLugField(
                              'model',
                              e.currentTarget.value ? Number(e.currentTarget.value) : null
                            )
                          }
                        >
                          <option className="hide" value="">
                            {emptyOptionName}
                          </option>
                          {lugModels.map((m) => (
                            <option key={Number(m.id)} value={Number(m.id)}>
                              {String(m.model ?? '')}
                            </option>
                          ))}
                        </select>
                        <span className="label">
                          <span>Model</span>
                        </span>
                      </div>
                    </li>
                    {/* Always locked: Attachment is not a choice, it is whatever
                        the selected Model comes with. Legacy's empty `<select>`
                        stub is there only so the row lines up with the real
                        selects around it; `.readonly-field-box` is that box
                        without the caret (ahuBoardOverrides.scss). */}
                    <li className="non-clickable">
                      <div className="single-line">
                        <span className="selection">
                          <span>{String(activeLug.attachment_name ?? '')}</span>
                        </span>
                        <span className="readonly-field-box" aria-hidden="true" />
                        <span className="label">
                          <span>Attachment</span>
                        </span>
                      </div>
                    </li>
                    <li className={lockedClass(false)}>
                      <div className="single-line">
                        <select
                          className="tunnel_lugs_field"
                          name="base_direction"
                          value={
                            activeLug.base_direction == null
                              ? ''
                              : String(activeLug.base_direction)
                          }
                          onChange={(e) =>
                            changeLugField(
                              'base_direction',
                              e.currentTarget.value ? Number(e.currentTarget.value) : null
                            )
                          }
                        >
                          <option className="hide" value="">
                            {emptyOptionName}
                          </option>
                          {lugBaseDirections.map((d) => (
                            <option key={Number(d.id)} value={Number(d.id)}>
                              {String(d.name ?? '')}
                            </option>
                          ))}
                        </select>
                        {/* Same KA-x rename (74853f97b8): the selected lug's
                            own direction reads "Location" on both AHU types,
                            leaving "Base Direction" for the tunnel setting. */}
                        <span className="label">
                          <span>Location</span>
                        </span>
                      </div>
                    </li>
                    <li className={lockedClass(false)}>
                      <div className="">
                        <span className="unit"> in</span>
                        <input
                          readOnly={activeLug.base_direction === 2}
                          disabled={activeLug.base_direction === 2}
                          data-type="number"
                          data-step="0.01"
                          className="tunnel_lugs_field"
                          name="x"
                          defaultValue={activeLug.x == null ? '' : String(activeLug.x)}
                          placeholder="varies..."
                          onBlur={(e) => {
                            const v = e.currentTarget.value;
                            if (v !== '') changeLugField('x', Number(v));
                          }}
                        />
                        <span className="label">
                          <span>X (tunnel length)</span>
                        </span>
                      </div>
                    </li>
                    <li className={lockedClass(false)}>
                      <div className="">
                        <span className="unit"> in</span>
                        <input
                          readOnly={activeLug.base_direction === 1}
                          disabled={activeLug.base_direction === 1}
                          data-type="number"
                          data-step="0.01"
                          className="tunnel_lugs_field"
                          name="y"
                          defaultValue={activeLug.y == null ? '' : String(activeLug.y)}
                          placeholder="varies..."
                          onBlur={(e) => {
                            const v = e.currentTarget.value;
                            if (v !== '') changeLugField('y', Number(v));
                          }}
                        />
                        <span className="label">
                          <span>Y (tunnel width)</span>
                        </span>
                      </div>
                    </li>
                    {showHint && (
                      <li>
                        <div className="brake-line">
                          <span className="label">
                            <span>
                              In order to view the lugs on the adjoining base(s) of a
                              tunnel, select a specific tunnel.
                            </span>
                          </span>
                        </div>
                      </li>
                    )}
                    <li className="empty-row" />
                    <li
                      className="toggle-cms-info always-visible"
                      onClick={() => setShowCmsInfo((v) => !v)}
                    >
                      <div className="">
                        <span className="label bold">
                          <span>Manufacturer&apos;s lug placement standards ...</span>
                        </span>
                      </div>
                    </li>
                    {showCmsInfo && (
                      <>
                        <li className="cms-info-item always-visible">
                          <div className="">
                            <span className="selection">
                              <span>{num2(activeLug.min_dist_from_base_edge)}</span>
                            </span>
                            <span className="label">Minimum Distance from base edge</span>
                            <span className="unit">in</span>
                          </div>
                        </li>
                        <li className="cms-info-item always-visible">
                          <div className="">
                            <span className="selection">
                              <span>{num2(activeLug.max_center_spacing)}</span>
                            </span>
                            <span className="label">Maximum lug center spacing</span>
                            <span className="unit">in</span>
                          </div>
                        </li>
                        <li className="cms-info-item always-visible">
                          <div className="">
                            <span className="selection">
                              <span>{String(activeLug.max_weight_per_lug ?? '')}</span>
                            </span>
                            <span className="label">Max weight per lug</span>
                            <span className="unit">lb</span>
                          </div>
                        </li>
                      </>
                    )}
                  </Fragment>
                )}
              </>
            )}
          </>
        )}
      </ul>
      {recalcButton}
      {defaultsPopup && (
        <UniversalPopup
          title={LUG_DEFAULTS_COPY[defaultsPopup].title}
          content={LUG_DEFAULTS_COPY[defaultsPopup].content}
          actionText={LUG_DEFAULTS_COPY[defaultsPopup].actionText}
          cancelText="CANCEL"
          onAction={() => confirmDefaults(defaultsPopup)}
          onCancel={() => setDefaultsPopup(null)}
        />
      )}
      {deletePopup && (
        <DeleteConfirmPopup
          title="Delete Lug?"
          content="If you choose to Delete, the lug and associated data will be deleted and will not be recoverable."
          onDelete={deleteActiveLugs}
          onCancel={() => setDeletePopup(false)}
        />
      )}
    </>
  );
};

export default LiftingLugPane;
